
'use client';

import { useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { getTechIdea, type GenerateTechIdeaOutput, type GenerateTechIdeaInput } from './actions';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType } from 'docx';

import { GradientText } from '@/components/ui/gradient-text';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Lightbulb, Sparkles, Target, DollarSign, Megaphone, Laptop, Server, Cloud, FileDown, FileText, Cpu, Layers } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Separator } from '@/components/ui/separator';


const formSchema = z.object({
  keywords: z.string().min(3, { message: 'Please enter at least 3 characters.' }),
});

export default function TechIdeaGeneratorPage() {
  const [idea, setIdea] = useState<GenerateTechIdeaOutput | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const ideaResultRef = useRef<HTMLDivElement>(null);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      keywords: '',
    },
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    setIsLoading(true);
    setIdea(null);
    const result = await getTechIdea(values);
    setIsLoading(false);

    if (result.success && result.data) {
      setIdea(result.data);
    } else {
      toast({
        variant: 'destructive',
        title: 'Error Generating Idea',
        description: result.message || 'An unexpected error occurred. Please try again.',
      });
    }
  }

  const handleDownloadPdf = async () => {
    const element = ideaResultRef.current;
    if (!element) return;
    const canvas = await html2canvas(element, { scale: 2 });
    const data = canvas.toDataURL('image/png');

    const pdf = new jsPDF('p', 'mm', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = canvas.width;
    const imgHeight = canvas.height;
    const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
    const imgX = (pdfWidth - imgWidth * ratio) / 2;
    const imgY = 0;

    pdf.addImage(data, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);
    pdf.save(`${idea?.name || 'tech-idea'}.pdf`);
  };

  const handleDownloadDocx = async () => {
    if (!idea) return;
    
    const doc = new Document({
        sections: [{
            properties: {},
            children: [
                new Paragraph({ text: idea.name, heading: HeadingLevel.TITLE, alignment: AlignmentType.CENTER }),
                new Paragraph({ text: idea.description, alignment: AlignmentType.CENTER, style: "IntenseQuote" }),
                new Paragraph({ text: "" }), // Spacer
                new Paragraph({ text: "Business Plan", heading: HeadingLevel.HEADING_1 }),
                new Paragraph({ text: "The Problem", heading: HeadingLevel.HEADING_2 }),
                new Paragraph(idea.businessPlan.problem),
                new Paragraph({ text: "The Solution", heading: HeadingLevel.HEADING_2 }),
                new Paragraph(idea.businessPlan.solution),
                new Paragraph({ text: "Target Audience", heading: HeadingLevel.HEADING_2 }),
                new Paragraph(idea.businessPlan.targetAudience),
                 new Paragraph({ text: "Monetization", heading: HeadingLevel.HEADING_2 }),
                new Paragraph(idea.businessPlan.monetization),
                new Paragraph({ text: "" }),
                new Paragraph({ text: "Marketing Strategy", heading: HeadingLevel.HEADING_1 }),
                new Paragraph({ text: "Marketing Channels", heading: HeadingLevel.HEADING_2 }),
                new Paragraph(idea.marketingStrategy.channels.join(', ')),
                new Paragraph({ text: "Initial Hook", heading: HeadingLevel.HEADING_2 }),
                new Paragraph(idea.marketingStrategy.initialHook),
                new Paragraph({ text: "" }),
                 new Paragraph({ text: "Suggested Tech Stack", heading: HeadingLevel.HEADING_1 }),
                 ...idea.techStack.flatMap(stack => [
                    new Paragraph({ text: stack.category, heading: HeadingLevel.HEADING_2 }),
                    new Paragraph(stack.technologies),
                ]),
            ],
        }],
    });

    const blob = await Packer.toBlob(doc);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${idea.name || 'tech-idea'}.docx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="container mx-auto max-w-5xl py-12 px-4 print:py-0">
      <div className="text-center mb-12 print:hidden">
        <h1 className="font-headline text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
          Tech Idea <GradientText>Generator</GradientText>
        </h1>
        <p className="mt-4 max-w-2xl mx-auto text-lg text-muted-foreground">
          Enter a few keywords (like "AI, healthcare, sustainability") and let our AI generate a unique tech startup idea for you.
        </p>
      </div>

      <Card className="max-w-2xl mx-auto print:hidden">
        <CardContent className="p-6">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col sm:flex-row gap-4">
              <FormField
                control={form.control}
                name="keywords"
                render={({ field }) => (
                  <FormItem className="flex-grow">
                    <FormControl>
                      <Input placeholder="e.g., AI, IoT, smart home" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button type="submit" disabled={isLoading} className="w-full sm:w-auto">
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2 h-4 w-4" />
                    Generate Idea
                  </>
                )}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>

      {idea && (
         <div className="mt-8 animate-in fade-in-50 max-w-5xl mx-auto">
            <div id="idea-result" ref={ideaResultRef} className="bg-card p-8 print:p-0">
                 <Card className="print:shadow-none print:border-none">
                     <CardHeader className="text-center">
                        <div className="flex justify-center items-center gap-4">
                             <Lightbulb className="text-primary h-12 w-12 flex-shrink-0" />
                             <div>
                                <CardTitle className="font-headline text-3xl">{idea.name}</CardTitle>
                                <CardDescription className="text-lg">{idea.description}</CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-8">
                        <div>
                            <h3 className="font-headline text-xl flex items-center gap-2 mb-2"><Lightbulb className="text-primary"/>Business Plan</h3>
                            <div className="space-y-4 text-muted-foreground pl-6">
                                <p><strong>Problem:</strong> {idea.businessPlan.problem}</p>
                                <p><strong>Solution:</strong> {idea.businessPlan.solution}</p>
                            </div>
                        </div>
                        <Separator />
                        <div className="grid md:grid-cols-2 gap-8">
                            <div>
                                <h3 className="font-headline text-xl flex items-center gap-2 mb-2"><Target className="text-primary"/>Target Audience</h3>
                                <p className="text-muted-foreground">{idea.businessPlan.targetAudience}</p>
                            </div>
                             <div>
                                <h3 className="font-headline text-xl flex items-center gap-2 mb-2"><DollarSign className="text-primary"/>Monetization</h3>
                                <p className="text-muted-foreground">{idea.businessPlan.monetization}</p>
                            </div>
                        </div>
                        <Separator />
                        <div>
                            <h3 className="font-headline text-xl flex items-center gap-2 mb-2"><Megaphone className="text-primary"/>Marketing Strategy</h3>
                             <div className="space-y-4 text-muted-foreground pl-6">
                                <p><strong>Channels:</strong> {idea.marketingStrategy.channels.join(', ')}</p>
                                <p><strong>Initial Hook:</strong> {idea.marketingStrategy.initialHook}</p>
                            </div>
                        </div>
                        <Separator />
                        <div>
                             <h3 className="font-headline text-xl flex items-center gap-2 mb-2"><Layers className="text-primary" />Suggested Tech Stack</h3>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-center">
                                {idea.techStack.map((stackItem, index) => (
                                    <Card key={index} className="bg-card-foreground/5 p-4">
                                        <p className="font-semibold">{stackItem.category}</p>
                                        <p className="text-muted-foreground text-sm">{stackItem.technologies}</p>
                                    </Card>
                                ))}
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
            <div className="flex justify-end gap-4 mt-4 print:hidden">
              <Button onClick={handleDownloadPdf} variant="outline">
                  <FileDown className="mr-2 h-4 w-4" />
                  Download PDF
              </Button>
              <Button onClick={handleDownloadDocx} variant="outline">
                  <FileText className="mr-2 h-4 w-4" />
                  Download DOCX
              </Button>
            </div>
        </div>
      )}
    </div>
  );
}
